import { ApiResponse, FormStore, GlobalStore } from "@roc/feature-app-core";
import { LoanService } from "@roc/feature-loans";
import { floridaCountiesRestriction, formatDate, getStateLicenseMatrixMessage, propertyCitiesRestriction, propertyStates, PURCHASE, REFINANCE } from "@roc/feature-utils";
import { action, computed, flow, makeObservable, observable } from "mobx";
import { PropertyTypeFormStore } from "./propertyTypeFormStore";
import { OneToolService } from "../../services/oneToolService";
import { isRestrictedCounty } from "../../utils/utils";

const FIELD_REQUIRED_ERROR = 'This field is required.';

const initialForm = {
  fields: {
    propertyStates: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    propertyCounties: {
      value: null,
      error: null,
      message: FIELD_REQUIRED_ERROR,
      rule: 'required',
    },
    loanPurpose: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    lastPurchaseDate: {
      value: null,
      error: null,
      rule: [{ required_if_one_of: ['loanPurpose', REFINANCE] }, { valid_date: true }],
      message: FIELD_REQUIRED_ERROR,
    },
    anyDebt: {
      value: null,
      error: null,
      rule: [{ required_if_one_of: ['loanPurpose', REFINANCE] }],
      message: FIELD_REQUIRED_ERROR,
    },
    totalDebtPayoff: {
      value: null,
      error: null,
      rule: [{ required_if_one_of: ['anyDebt', true] }],
      message: FIELD_REQUIRED_ERROR,
    },
    loanRecourse: {
      value: '',
      error: null,
      message: FIELD_REQUIRED_ERROR,
    },
    acquisitionPrice: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    minimumAsIsValue: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    maximumAsIsValue: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    capitalImprovements: {
      value: '',
      error: null,
      rule: [{ required_if_one_of: ['loanPurpose', REFINANCE] }],
      message: FIELD_REQUIRED_ERROR,
    },
    totalGrossMonthlyRent: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    totalGrossAnnualTaxes: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    totalGrossAnnualInsurance: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    totalAnnualHOADues: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    cashOut: {
      value: '',
      error: null,
      rule: [{ required_if_one_of: ['anyDebt', true] }],
      message: FIELD_REQUIRED_ERROR,
    },
    section8: {
      value: '',
      error: null,
      rule: 'required',
    },
  },
  meta: {
    key: null,
    editMode: false,
    isValid: false,
    error: null,
  },
};


export class PortfolioInformationFormStore extends FormStore {
  private globalStore: GlobalStore;
  private loanService: LoanService;
  private oneToolService = new OneToolService();
  public allCounties;
  propertyTypeFormStores: PropertyTypeFormStore[];
  stateLicenseMessage: string = null;
  private stateLicenseMatrix: any[] = null;


  constructor(globalStore) {
    super(initialForm, globalStore);
    this.globalStore = globalStore;
    this.loanService = new LoanService();

    makeObservable(this, {
      stateLicenseMessage: observable,
      loadStateLicenseMessage: flow,
      fetchStateCounties: flow,
      propertyTypeFormStores: observable,
      addNewPropertyType: action,
      deletePropertyType: action,
      totalNoOfProperties: computed,
      totalEstimatedAsIsValue: computed,
      totalAnnualIncome: computed,
      annualExpenses: computed,
      annualNOI: computed,
      allCounties: observable,
      getCounties: action,
      validateAndCleanupUnusedFields: action,
      getLoanPortfolioInformation: action,
      validateFields: action,
      cleanupUnusedFields: action,
      loadPortfolioInformation: action,
      isRestrictedBaltimoreCity: computed,
    });
  }

  reset() {
    super.reset();
    this.propertyTypeFormStores = [];
  }

  *fetchStateCounties() {
    try {
      const response: ApiResponse = yield this.loanService.getStateCounties();
      this.allCounties = response.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting state counties.',
      });
    }
  }

  /**
   * Loads the state license matrix (once) and derives the licensing banner message
   * from the selected property state(s), based on companyLicenseRequired/originatorLicenseRequired flags.
   */
  *loadStateLicenseMessage() {
    try {
      if (this.stateLicenseMatrix === null) {
        const response = yield this.oneToolService.getAllStateLicenseMatrix();
        this.stateLicenseMatrix = response?.data?.data ?? [];
      }
      const raw = this.form.fields.propertyStates.value;
      const states = (Array.isArray(raw) ? raw : [raw])
        .filter(Boolean)
        .map(s => s.toUpperCase());
      const entries = this.stateLicenseMatrix.filter(m => states.includes(m.state));
      const strongest =
        entries.find(e => e.companyLicenseRequired && e.originatorLicenseRequired) ||
        entries.find(e => e.companyLicenseRequired) ||
        null;
      this.stateLicenseMessage = getStateLicenseMatrixMessage(strongest, this.globalStore.lenderInfo?.isInternal);
    } catch {
      this.stateLicenseMessage = null;
    }
  }

  getProperties() {
    return this.propertyTypeFormStores.map(form => form.getProperty());
  }

  getCounties() {
    if (!this.allCounties) {
      return [];
    }
    const propertyStateId = propertyStates.find(
      x => x.value === this.form.fields.propertyStates.value
    )?.id;
    const states = propertyStateId ? [propertyStateId] : [];
    let selectedStateCounties = [];
    if (states && states.length > 0) {
      selectedStateCounties = states.reduce(
        (c, s) => [...c, ...(this.allCounties[s] ?? [])],
        []
      );
    }

    const counties = [];
    if (selectedStateCounties && selectedStateCounties.length > 0) {
      selectedStateCounties.sort().map(county => {
        counties.push({ value: county, label: county });
      });
    }

    return counties;
  }

  addNewPropertyType(initialValues = {}) {
    const newForm = new PropertyTypeFormStore(this.globalStore);
    newForm.loadForm(initialValues);
    this.propertyTypeFormStores.push(newForm);
  }

  deletePropertyType(key) {
    this.propertyTypeFormStores = this.propertyTypeFormStores.filter(store => store.key !== key);
  }

  get totalNoOfProperties() {
    if (!this.propertyTypeFormStores?.length) {
      return 0;
    }

    let res = 0;

    this.propertyTypeFormStores.forEach(store => {
      res += +(store.form.fields.numberOfProperties.value || 0);
    });

    return res;
  }

  get totalEstimatedAsIsValue() {
    if (!this.propertyTypeFormStores?.length) {
      return 0;
    }

    let res = 0;

    this.propertyTypeFormStores.forEach(store => {
      res += +(store.form.fields.estimatedAsIsValue.value || 0);
    });

    return res;
  }

  get totalAnnualIncome() {
    const rent = isNaN(Number(this.form.fields.totalGrossMonthlyRent.value))
      ? 0
      : Number(this.form.fields.totalGrossMonthlyRent.value);
    const totalAnnualIncome = rent * 12;
    return totalAnnualIncome;
  }
  get annualExpenses() {
    const {
      totalGrossAnnualTaxes,
      totalGrossAnnualInsurance,
      totalAnnualHOADues,
    } = this.form.fields;
    const taxes = isNaN(Number(totalGrossAnnualTaxes.value))
      ? 0
      : Number(totalGrossAnnualTaxes.value);
    const insurance = isNaN(Number(totalGrossAnnualInsurance.value))
      ? 0
      : Number(totalGrossAnnualInsurance.value);
    const hoa = isNaN(Number(totalAnnualHOADues.value))
      ? 0
      : Number(totalAnnualHOADues.value);

    const totalAnnualExpenses =
      taxes +
      insurance +
      hoa;

    return totalAnnualExpenses;
  }
  get annualNOI() {
    return this.totalAnnualIncome - this.annualExpenses;
  }

  validateAndCleanupUnusedFields() {
    this.cleanupUnusedFields();
    return this.validateFields();
  }

  validateFields() {
    let isValid = true;

    if (this.propertyTypeFormStores.length === 0) {
      isValid = false;
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please enter at least one property type.',
      });
    }

    this.propertyTypeFormStores.forEach(store => {
      store.runFormValidation();
      isValid = isValid && store.form.meta.isValid;
    });

    this.runFormValidation();
    if (!this.form.fields.propertyCounties.value) {
      this.form.fields.propertyCounties.error = FIELD_REQUIRED_ERROR;
    }

    isValid = isValid && this.form.meta.isValid;

    if (!isValid) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please complete all the required fields.',
      });
    }

    if (this.isRestrictedBaltimoreCity) {
      isValid = false;
      this.globalStore.notificationStore.showWarningNotification({
        message: propertyCitiesRestriction.message,
      });
    }
    if (this.isRestrictedFloridaCounty) {
      isValid = false;
      this.globalStore.notificationStore.showWarningNotification({
        message: floridaCountiesRestriction.message,
      });
    }

    return isValid;
  }

  cleanupUnusedFields() {
    const isPurchase = this.form.fields.loanPurpose.value === PURCHASE;

    if (isPurchase) {
      this.onFieldChange("lastPurchaseDate", null);
      this.onFieldChange("capitalImprovements", "");
      this.onFieldChange("anyDebt", null);
    }
  }

  getLoanPortfolioInformation() {
    const formValues = this.getFormValues();
    return {
      ...formValues,
      properties: this.getProperties(),
      anyDebt: (formValues.anyDebt === true ||
        formValues.anyDebt === 'true' ||
        formValues.anyDebt === 'Yes' ||
        formValues.anyDebt === 1),
      purchaseDate: formatDate(formValues.lastPurchaseDate, 'MM/dd/yyyy'),
    };
  }

  loadPortfolioInformation(quoteData) {
    this.loadForm({
      ...quoteData.portfolioInformation,
      anyDebt: quoteData.portfolioInformation?.anyDebt,
      lastPurchaseDate: quoteData.portfolioInformation?.purchaseDate,
      propertyCounties: quoteData.portfolioInformation?.propertyCounties
    });
    quoteData.portfolioInformation?.properties.map(property => {
      const propertyTypeFormStore = new PropertyTypeFormStore(this.globalStore);
      propertyTypeFormStore.loadForm(property);
      this.propertyTypeFormStores.push(propertyTypeFormStore);
    })
  }

  get isRestrictedBaltimoreCity() {
    return this.form.fields.propertyCounties?.value?.some(county => propertyCitiesRestriction.counties.includes(county?.value.trim().toLowerCase()));
  }
  get isRestrictedFloridaCounty() {
    return this.form.fields.propertyCounties?.value?.some(county => isRestrictedCounty(county?.value.trim().toLowerCase(), floridaCountiesRestriction.counties, this.form.fields.propertyStates.value, floridaCountiesRestriction.states));
  }
}
